import * as Sentry from "@sentry/react";

// The Sentry feedback form type is not explicitly exported from the SDK,
// so we define a minimal interface for it here based on the documentation.
type SentryForm = {
  appendToDom: () => void;
  open: () => void;
  close: () => void;
};

type SentryFeedbackIntegration = {
  createForm: () => Promise<SentryForm>;
};

let formRef: SentryForm | null = null;

const getOrCreateForm = async (): Promise<SentryForm | null> => {
  if (formRef) {
    return formRef;
  }

  const feedback = Sentry.getFeedback() as SentryFeedbackIntegration | undefined;

  if (!feedback) {
    console.warn("Sentry Feedback Integration not found. Please ensure it is initialized in your Sentry config.");
    return null;
  }

  try {
    const form = await feedback.createForm();
    form.appendToDom();
    formRef = form;
    return formRef;
  } catch (error) {
    console.error("Failed to create Sentry feedback form:", error);
    return null;
  }
};

/**
 * Shows or hides the Sentry user feedback dialog.
 *
 * This function manages the lifecycle of the feedback form, creating it
 * on first use and then controlling its visibility.
 *
 * @param {boolean} isOpen - Pass true to open the dialog, false to close it.
 */
export const toggleErrorReport = async (isOpen: boolean) => {
  const form = await getOrCreateForm();
  if (form) {
    if (isOpen) {
      form.open();
    } else {
      form.close();
    }
  }
};

export const toggleErrorReporting = () => {
  // Standalone feedback events carry no breadcrumbs. If no real event exists yet
  // this page lifecycle, snapshot the current scope as an info-level message so
  // beforeSend (src/instrument.js) can link the feedback via associated_event_id. The
  // dedicated fingerprint + info level keep these out of error counts.
  if (!Sentry.lastEventId()) {
    Sentry.captureMessage("User feedback context snapshot", {
      level: "info",
      tags: { feedback_context: "synthetic" },
      fingerprint: ["feedback-context-snapshot"],
    });
  }
  return toggleErrorReport(true);
};
